import { FC, useContext } from 'react'
import { slugs } from '@/constants/slugs'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useShouldShowLimitedEvent } from '@/views/TicketCheckoutViews/ShowtimesView/components/Calendar/hooks'
import EarlyBirdModal from '@/views/TicketCheckoutViews/ShowtimesView/components/modals/EarlyBirdModal'
import { LimitedDateModal } from '@/views/TicketCheckoutViews/ShowtimesView/components/modals/LimitedDateModal'
import { NoFeesModal } from '@/views/TicketCheckoutViews/ShowtimesView/components/modals/NoFeesModal'
import { LimitedDateModalContext } from '@/views/TicketCheckoutViews/ShowtimesView/contexts/LimitedDateModalContext'
import { useTheatricalModalController } from '@/views/TicketCheckoutViews/ShowtimesView/hooks/useTheatricalModalController'
import { useTheatricalPromotionContext } from '@/views/TicketCheckoutViews/components/TheatricalPromotionContext'
import { getCustomCodeComponent } from '@/views/TicketCheckoutViews/components/TheatricalPromotionContext/utils'
import { Flow } from '@/views/TicketCheckoutViews/utils'
import CustomImageModal from './CustomImageModal'
import { DavidNoFeesModal } from './DavidNoFeesModal'
import { GiveawayModal } from './GiveawayModal/GiveawayModal'

interface Props {
  theatricalSlug: string
}

export const TicketPromoModals: FC<Props> = ({ theatricalSlug }) => {
  const { promo } = useTheatricalPromotionContext()

  const limitedDateModalContext = useContext(LimitedDateModalContext)
  const { isModalOpen: isLimitedModalOpen, shouldShow } = limitedDateModalContext
  const { hasLimitedEvent } = useShouldShowLimitedEvent(theatricalSlug)

  const [isModalOpen, closeModal] = useTheatricalModalController(promo, theatricalSlug, shouldShow)
  const { query } = useSafeRouter()

  const isGiftFlow =
    query.flow === Flow.GUILD_GIFT || query.flow === Flow.GIFT_TICKETS || query.flow === Flow.GIFT_GUILD_TICKETS

  const renderDisabled = isGiftFlow || isLimitedModalOpen || !promo

  const renderContent = () => {
    if (renderDisabled) return null
    switch (promo?.modal?.modalType) {
      case 'early_bird': {
        if (promo.promoType !== 'early_bird') return null
        return (
          <EarlyBirdModal
            onClose={closeModal}
            open={isModalOpen}
            backgroundImage={promo.modal.backgroundImage}
            accentColor={promo.modal.accentColor}
            earlyBirdDiscount={promo.publicDiscount}
            openingWeek={promo.modal.openingWeek}
            code={promo.publicDiscount.code}
          />
        )
      }
      case 'no_fees': {
        if (promo.promoType !== 'no_fees') return null
        return theatricalSlug === slugs.david ? (
          <DavidNoFeesModal
            code={promo.publicDiscount.code}
            open={isModalOpen}
            onClose={closeModal}
            backgroundImage={promo.modal.backgroundImage}
            accentColor={promo.modal.accentColor}
            backgroundIsDark={![slugs.david, slugs.iWasAStranger].includes(theatricalSlug)}
          />
        ) : (
          <NoFeesModal
            code={promo.publicDiscount.code}
            open={isModalOpen}
            onClose={closeModal}
            backgroundImage={promo.modal.backgroundImage}
            accentColor={promo.modal.accentColor}
            noFeesDiscount={promo.publicDiscount}
            backgroundIsDark={![slugs.david, slugs.iWasAStranger, slugs.soloMio].includes(theatricalSlug)}
          />
        )
      }
      case 'giveaway':
        return <GiveawayModal isOpen={isModalOpen} onClose={closeModal} theatricalSlug={theatricalSlug} />
      case 'custom_image':
        return (
          <CustomImageModal
            open={isModalOpen}
            onClose={closeModal}
            {...promo.modal}
            alt={`${promo.code} modal`}
            backgroundIsDark={promo.code === 'IWASBOGO'}
          />
        )
      case 'custom_code': {
        const customCodeComponent = getCustomCodeComponent(promo.modal.name)
        return typeof customCodeComponent === 'function'
          ? customCodeComponent(isModalOpen, closeModal)
          : customCodeComponent
      }
      default:
        return null
    }
  }

  return (
    <>
      {!isLimitedModalOpen && renderContent()}
      {hasLimitedEvent && !isGiftFlow && (
        <LimitedDateModal
          isOpen={isLimitedModalOpen}
          onClose={() => {
            limitedDateModalContext.closeModal()
          }}
          theatricalSlug={theatricalSlug}
        />
      )}
    </>
  )
}

export default TicketPromoModals
